import React, { useEffect } from "react";

const SKILL_CATEGORIES = [
    {
        title: "Cloud & Monitoring",
        skills: ["Azure Monitor", "Application Insights", "Azure Log Analytics", "Metrics Analysis", "Logs & Traces", "Performance Monitoring", "Availability Monitoring", "Azure Cost Management", "Root Cause Analysis"],
    },
    {
        title: "Development",
        skills: ["JavaScript", "React.js", "Node.js", "Express.js", "MongoDB", "MySQL", "C", "C++", "HTML", "CSS"],
    },
    {
        title: "Tools & Process",
        skills: ["Git", "GitHub", "VS Code", "Azure Portal", "Kusto (KQL)", "ITIL Processes", "Technical Documentation", "SOPs & Runbooks"],
    },
];

const RESPONSIBILITIES = [
    "Continuous monitoring and operational support using Azure Monitor and Azure Log Analytics across large-scale enterprise environments.",
    "Analyzing telemetry data, metrics, and logs to identify anomalies, investigate incidents, and support Root Cause Analysis (RCA).",
    "Tracking CPU, memory, network usage, HTTP requests, and failure patterns to reduce downtime and improve recovery times.",
    "Collaborating with development, security, and operations teams to improve system stability and follow ITIL-based processes.",
    "Creating and maintaining technical documentation, SOPs, and operational workflows to standardize support.",
    "Monitoring performance and availability for Honeywell Lenel Physical Access Control Systems where reliability is critical.",
];

function Experience() {
    useEffect(() => { document.title = "Experience — Raviraj Kumar Tiwari"; }, []);

    return (
        <div className="min-h-screen" style={{ background: "var(--clr-surface)" }}>
            {/* Page Header */}
            <section className="px-6 py-20 section-light">
                <div className="max-w-6xl mx-auto">
                    <p className="section-label mb-4">Professional Record</p>
                    <h1 className="display-heading max-w-xl">
                        Technical Experience
                    </h1>
                    <p className="mt-4 text-on-surface-variant max-w-2xl leading-relaxed">
                        A chronological record of cloud infrastructure engineering, system monitoring,
                        and high-stakes observability work.
                    </p>
                </div>
            </section>

            {/* Experience Card */}
            <section className="px-6 py-20" style={{ background: "var(--clr-surface)" }}>
                <div className="max-w-4xl mx-auto">
                    <p className="section-label mb-10">Career Path</p>
                    <div
                        className="card-node card-node-accent"
                        style={{ background: "var(--clr-surface-lowest)" }}
                    >
                        {/* Role header */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-on-surface">
                                    Cloud &amp; Infrastructure Engineer
                                </h2>
                                <p className="text-base font-medium mt-1" style={{ color: "var(--clr-primary)" }}>
                                    LTIMindtree
                                </p>
                                <p className="text-sm text-on-surface-variant mt-0.5">
                                    Client: Honeywell (Lenel Support — CIS-FMB)
                                </p>
                            </div>
                            <span className="chip self-start">Feb 2025 — Present</span>
                        </div>

                        {/* Responsibilities */}
                        <ul className="space-y-3 mb-8">
                            {RESPONSIBILITIES.map((r) => (
                                <li key={r} className="flex items-start gap-3 text-sm text-on-surface-variant leading-relaxed">
                                    <span
                                        className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0"
                                        style={{ background: "var(--clr-primary)" }}
                                    />
                                    {r}
                                </li>
                            ))}
                        </ul>

                        {/* Context note */}
                        <div
                            className="rounded-[4px] p-4 text-sm text-on-surface-variant italic"
                            style={{ background: "var(--clr-surface-container)" }}
                        >
                            This role has provided strong hands-on exposure to real-world cloud monitoring,
                            incident management, and enterprise production support at scale.
                        </div>
                    </div>
                </div>
            </section>

            {/* Skills Section */}
            <section className="px-6 py-20 section-light">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-14">
                        <p className="section-label mb-3">Technical Skills</p>
                        <h2 className="headline">Tools I Work With Daily</h2>
                        <p className="mt-3 text-sm text-on-surface-variant max-w-xl leading-relaxed">
                            Cloud monitoring, full-stack development, and engineering tools that power my work.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SKILL_CATEGORIES.map(({ title, skills }) => (
                            <div key={title} className="card-node" style={{ background: "var(--clr-surface-lowest)" }}>
                                <p className="section-label mb-5">{title}</p>
                                <div className="flex flex-wrap gap-2">
                                    {skills.map((skill) => (
                                        <span key={skill} className="chip">{skill}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Experience;
